import type { FC } from 'react';
import { Avatar } from 'antd';
import { TbUserCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ActiveAvatarHoverDropdown } from '@/shared/components/dropdowns';
import { AvatarSkeleton } from '@/shared/components/skeletons';
import { usePresenceStore } from '@/stores';

import { getPresenceAvatarStyle } from './presence';

interface CompactActiveAvatarUser {
    avatar?: string | null;
    emailAddress?: string;
    firstName?: string;
    id?: string;
    lastName?: string;
}

interface CompactActiveAvatarMember {
    user?: CompactActiveAvatarUser;
}

export interface CompactActiveAvatarCollaborator {
    id?: string;
    member?: CompactActiveAvatarMember;
    user?: CompactActiveAvatarUser;
}

export interface CompactActiveAvatarGroupProps {
    collaborators?: readonly CompactActiveAvatarCollaborator[];
    loading?: boolean;
    maxCount?: number;
    showResourceContext?: boolean;
    size?: number;
    tooltipPlacement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
}

const collaboratorUser = (collaborator: CompactActiveAvatarCollaborator) =>
    collaborator.member?.user ?? collaborator.user;

const collaboratorName = (collaborator: CompactActiveAvatarCollaborator) => {
    const user = collaboratorUser(collaborator);

    return [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.emailAddress || 'Collaborator';
};

const CompactActiveAvatarGroup: FC<CompactActiveAvatarGroupProps> = ({
    collaborators = [],
    loading = false,
    maxCount = 3,
    showResourceContext = false,
    size = 18,
    tooltipPlacement = 'bottomLeft',
}) => {
    const { token } = useAntDesignTheme();
    const presenceUsers = usePresenceStore((state) => state.users);

    if (loading) {
        return (
            <span
                aria-label='Loading collaborators'
                style={{ alignItems: 'center', display: 'inline-flex', height: size, lineHeight: 0 }}
            >
                <AvatarSkeleton size={size} style={{ height: size, width: size }} />
            </span>
        );
    }

    if (collaborators.length === 0) {
        return null;
    }

    return (
        <Avatar.Group
            max={{
                count: maxCount,
                style: {
                    backgroundColor: token.colorBgBase,
                    border: `1px solid ${token.colorBorder}`,
                    color: token.colorTextTertiary,
                    fontSize: size <= 16 ? 8 : 9,
                    height: size,
                    lineHeight: `${size}px`,
                    width: size,
                },
            }}
            size={size}
            style={{
                alignItems: 'center',
                display: 'inline-flex',
                flex: 'none',
                height: size,
                lineHeight: 0,
                verticalAlign: 'middle',
            }}
        >
            {collaborators.map((collaborator, index) => {
                const user = collaboratorUser(collaborator);
                const displayName = collaboratorName(collaborator);
                const status = user?.id ? presenceUsers[user.id]?.status : undefined;

                return (
                    <ActiveAvatarHoverDropdown
                        collaborator={collaborator}
                        key={collaborator.id ?? user?.id ?? `${displayName}-${index}`}
                        showResourceContext={showResourceContext}
                        tooltipPlacement={tooltipPlacement}
                    >
                        <Avatar
                            alt={displayName}
                            icon={<TbUserCircle size='0.75rem' />}
                            size={size}
                            src={user?.avatar}
                            style={{
                                backgroundColor: token.colorBgBase,
                                border: `1px solid ${token.colorBorder}`,
                                color: token.colorTextTertiary,
                                ...getPresenceAvatarStyle(status),
                            }}
                        >
                            {displayName.charAt(0).toLocaleUpperCase()}
                        </Avatar>
                    </ActiveAvatarHoverDropdown>
                );
            })}
        </Avatar.Group>
    );
};

export default CompactActiveAvatarGroup;
